import { SECTION_PAGE } from "./useSectionPagination";
import { readStickyState, useStickyState } from "./useStickyState";

/** The key a grid's rows-per-page choice is stored under. */
function pageSizeKey(tableId: string): string {
  return `table-page-size-${tableId}`;
}

/**
 * Reads the page size {@link useStickyPageSize} would hand a grid, for code
 * that runs before the grid mounts — a route loader preloading its first page
 * must ask for the same number of rows, or the grid loads again on mount.
 */
export function readStickyPageSize(
  tableId: string,
  options: readonly number[],
  defaultSize = SECTION_PAGE,
): number {
  const stored = readStickyState<number>(defaultSize, pageSizeKey(tableId));
  return options.includes(stored) ? stored : defaultSize;
}

/**
 * A grid's rows-per-page choice, remembered under its table id. Feed the pair
 * to {@link useSectionPagination}'s `pageSize` / `onPageSizeChange` to make an
 * accordion grid's page size grid-wide and persistent.
 *
 * `options` are the sizes the grid's footer offers: a stored value outside
 * them (stale or hand-edited) reads as the default rather than requesting an
 * odd page.
 */
export function useStickyPageSize(
  tableId: string,
  options: readonly number[],
  defaultSize = SECTION_PAGE,
): readonly [number, (size: number) => void] {
  const [stored, setStored] = useStickyState<number>(
    defaultSize,
    pageSizeKey(tableId),
  );
  return [options.includes(stored) ? stored : defaultSize, setStored];
}
